import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Alert, Box, InputAdornment, Snackbar, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchOssActions, processOssAction } from "../../api/ossApi";
import { OssActionFilterTabs } from "../../components/actions/OssActionFilterTabs";
import { OssActionProcessDialog } from "../../components/actions/OssActionProcessDialog";
import { OssActionQueueList } from "../../components/actions/OssActionQueueList";
import { OssMetricCards } from "../../components/common/OssMetricCards";
import { OssPanel } from "../../components/common/OssPanel";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { OssOrderInlineDetail } from "../../components/orders/OssOrderInlineDetail";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";
import { filterActionItemsByTab, getActionTabCounts } from "../../utils/actionFilterUtils";
import { getActionItems, isMockAction, mockActions } from "../../utils/actionQueueUtils";

const initialActionData = {
  items: [],
  pagination: { page: 1, total: 0, totalPages: 1 },
};

export const OssActionQueue = () => {
  const { group_code, project_code } = useParams();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("ALL");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [processTarget, setProcessTarget] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const { data, loading, error, setError, load } = useOssAsyncData({
    fetcher: () => fetchOssActions(group_code, project_code, { search, page }),
    fallbackData: { ...initialActionData, items: mockActions },
    errorMessage: "조치 필요 항목을 불러오지 못했습니다.",
    deps: [group_code, project_code, search, page],
    debounceMs: 250,
  });

  const items = useMemo(() => getActionItems(data.items, search), [data.items, search]);
  const tabCounts = useMemo(() => getActionTabCounts(items), [items]);
  const filteredItems = useMemo(() => filterActionItemsByTab(items, activeTab), [activeTab, items]);
  const metrics = useMemo(() => {
    return [
      { label: "전체 조치", value: Number(data.pagination?.total || tabCounts.ALL || 0).toLocaleString(), unit: "건", tone: "#6c5ce7", iconName: "warningIcon", helper: "처리 목록 기준", filterKey: "ALL" },
      { label: "기한 초과", value: Number(tabCounts.OVERDUE || 0).toLocaleString(), unit: "건", tone: "#991b1b", iconName: "warningIcon", helper: "즉시 확인", filterKey: "OVERDUE" },
      { label: "납기 위험", value: Number(tabCounts.DELIVERY_RISK || 0).toLocaleString(), unit: "건", tone: "#ef4444", iconName: "warningIcon", helper: "고위험 포함", filterKey: "DELIVERY_RISK" },
      { label: "계획 확인", value: Number(tabCounts.PLAN_REVIEW || 0).toLocaleString(), unit: "건", tone: "#f59e0b", iconName: "checklistIcon", helper: "생산계획 확인", filterKey: "PLAN_REVIEW" },
      { label: "변경 검토", value: Number(tabCounts.CHANGE_REVIEW || 0).toLocaleString(), unit: "건", tone: "#8b5cf6", iconName: "checklistIcon", helper: "변경 영향 검토", filterKey: "CHANGE_REVIEW" },
    ];
  }, [data.pagination?.total, tabCounts]);

  const submitAction = async (payload) => {
    if (payload.lifecycleSaved) { await load().catch(() => {}); return true; }
    if (!processTarget?.order_line_id || isMockAction(processTarget)) return false;
    setSubmitting(true);
    setError("");
    try {
      await processOssAction(group_code, project_code, processTarget.order_line_id, {
        ...payload,
        userKey: "order.manager",
      });
      setToast(payload.actionStatus === "NOTE" ? "조치 이력을 저장했습니다." : payload.nextCheckDate ? "다음 확인일을 예약했습니다." : "조치 처리를 완료했습니다.");
      await load().catch(() => {});
      return true;
    } catch (e) {
      setError(e.response?.data?.message || "조치 처리 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      className="oss-page oss-page--actions"
      spacing={2}
      sx={{
        height: { md: "100%" },
        minHeight: 0,
        overflow: { md: "hidden" },
      }}
    >
      <OssPageHeader
        title="조치 필요 주문"
        iconName="warningIcon"
      />

      {error && <Alert severity="warning">{error}</Alert>}

      <OssMetricCards
        items={metrics}
        columns={{ xs: 2, md: 5, lg: 5 }}
        activeKey={activeTab}
        onItemClick={(next) => {
          setActiveTab(next);
          setPage(1);
        }}
      />

      <OssPanel className="oss-list-panel" sx={{ flex: 1, minHeight: 0, p: 1.25, overflow: "hidden", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
        <Box
          className="oss-filter-toolbar oss-action-search"
          sx={{
            p: 1.5,
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            bgcolor: "#fbfbfe",
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flexShrink: 0,
            mb: 1.25,
          }}
        >
          <TextField
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
              setActiveTab("ALL");
            }}
            size="small"
            placeholder="주문번호, 거래처, 품목, 조치명 검색"
            sx={{ width: { xs: 260, md: 420 }, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end" }}>
            <OssActionFilterTabs
              value={activeTab}
              counts={tabCounts}
              onChange={(next) => {
                setActiveTab(next);
                setPage(1);
              }}
            />
          </Box>
        </Box>

        <OssActionQueueList
          items={filteredItems}
          loading={loading}
          pagination={activeTab === "ALL" ? data.pagination : { page: 1, totalPages: 1 }}
          page={page}
          onPageChange={setPage}
          onProcess={setProcessTarget}
          onOpenDetail={(orderId) => setSelectedOrderId(String(orderId || ""))}
        />
      </OssPanel>

      <OssActionProcessDialog
        open={Boolean(processTarget)}
        item={processTarget}
        groupCode={group_code}
        projectCode={project_code}
        loading={submitting}
        onClose={() => setProcessTarget(null)}
        onSubmit={submitAction}
      />

      <OssOrderInlineDetail
        open={Boolean(selectedOrderId)}
        groupCode={group_code}
        projectCode={project_code}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId("")}
        onChanged={load}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Stack>
  );
};
